import type { DocumentPurpose } from '@/types/document'
import type { MutationVars } from '@/types/request'
import { useMutation, useQuery } from '@tanstack/vue-query'
import deepmerge from 'deepmerge'
import { cloneDeep, debounce } from 'lodash-es'
import { v4 as uuidv4 } from 'uuid'

import { computed, reactive, ref } from 'vue'
import { CRUD_MUTATION_PREFIX, ReApi, ReApiVersion, ReEndpoint } from '@/composables/query'
import { useCurUser } from '@/composables/useCurUser'
import enums from '@/plugins/enums.js'
import store from '@/store'

import { fileToDataURL } from './utils/blob.js'
import { reactiveAny } from './utils/reactive.js'

export interface Document {
  document_ref: string
  purpose: string
  filename: string
  content_type: string
  dataURL?: string
  room_id?: string
  valid?: boolean | null
  source?: string
  validation_purpose?: string | null
  validation_remark?: string | null
  validation_by?: string | null
}

function useDocumentList(curRequestInit: any, documentFilter: Parameters<Array<Document>['filter']>[0] | undefined = undefined, cacheOnly = false) {
  const curRequestRef = ref(curRequestInit)

  const queryKey = computed(() => [
    ReApi.VALUATION,
    ReApiVersion.CURRENT,
    ReEndpoint.REQUEST,
    curRequestRef?.value.requestRef,
    'document',
  ])
  const query = useQuery<Document[]>({
    queryKey,
    enabled: computed(() => !!curRequestRef.value.requestRef && curRequestRef.value.isLoaded && !cacheOnly),
    select: (data) => {
      return documentFilter !== undefined && data?.filter ? data.filter(documentFilter) : data
    },
  })

  const mutation = useMutation<any, Error, MutationVars>({
    mutationKey: [CRUD_MUTATION_PREFIX, ...queryKey.value],
  })

  const uploadDoc = async ({
    document,
    purpose,
    source,
    room_id,
  }: {
    document: Document | File
    purpose: string
    source?: string
    room_id?: string
  }) => {
    const data = new FormData()
    data.append('attachment', document as Document & Blob)

    return mutation.mutateAsync({
      verb: 'POST',
      data,
      queryKey: [...queryKey.value, { purpose, room_id, source }],
      idFieldName: 'document_ref',
    })
  }

  // TODO: move this logic to query side
  const addDoc = async (file: File, purpose: string, room_id: string | null = null) => {
    const dataURL = await fileToDataURL(file)
    const document: Document = {
      purpose,
      filename: file.name,
      content_type: file.type,
      dataURL,
      document_ref: `client-${uuidv4()}`,
    }
    if (room_id) {
      document.room_id = room_id
    }
    return uploadDoc({ document, purpose })
  }

  const deleteDoc = (documentRef: string) => {
    return mutation.mutateAsync({
      verb: 'DELETE',
      queryKey: [...queryKey.value, documentRef],
      idFieldName: 'document_ref',
    })
  }

  const DEBOUNCE_TIME = 500
  const patchData = ref<Record<string, Partial<Document>>>({})

  const debouncedPatch = debounce(() => {
    const patchDataCopy = cloneDeep(patchData.value)
    patchData.value = {}
    Object.entries(patchDataCopy).forEach(([documentRef, data]) => {
      patchDoc(documentRef, data, false)
    })
  }, DEBOUNCE_TIME)

  const patchDoc = (documentRef: string, data: Partial<Document>, debounce = false) => {
    if (debounce) {
      patchData.value[documentRef] = deepmerge(patchData.value[documentRef] || {}, data)
      debouncedPatch()
      return
    }
    return mutation.mutateAsync({
      verb: 'PATCH',
      data,
      queryKey: [...queryKey.value, documentRef],
      idFieldName: 'document_ref',
    })
  }

  const createStreetView = () => {
    return mutation.mutateAsync({
      verb: 'POST',
      queryKey: [...queryKey.value, 'streetview-snapshot'],
    })
  }

  const documents = computed(() => query.data.value ?? [])

  // Returns a reactive list of documents
  const rdocuments = computed(() => {
    return documents.value.map(
      d =>
        new Proxy(d, {
          get(_target, _key: keyof Document) {
            if (patchData.value[d.document_ref] && patchData.value[d.document_ref][_key]) {
              return patchData.value[d.document_ref][_key]
            }

            // @ts-expect-error arguments type
            // eslint-disable-next-line prefer-rest-params
            return Reflect.get(...arguments)
          },

          set(_target, _key: keyof Document, _value: unknown) {
            const valid = _key === 'valid' ? _value : d.valid

            if (
              // valid !== null &&
              ['valid', 'validation_purpose', 'validation_remark', 'validation_by'].includes(_key)
            ) {
              patchDoc(
                d.document_ref,
                {
                  valid: valid as boolean,
                  validation_purpose: d.validation_purpose,
                  validation_remark: d.validation_remark,
                  validation_by: d.validation_by,
                  [_key]: _value,
                },
                true,
              )
              return true
            }

            // @ts-expect-error arguments type
            // eslint-disable-next-line prefer-rest-params
            return Reflect.set(...arguments)
          },
        }),
    )
  })

  const byRef = (document_ref: string) => documents.value.find(d => d.document_ref === document_ref)

  // TODO: add offline support
  const getImageDataURL = (document_ref: string) => {
    // if (document_ref.startsWith('client-')) {
    return byRef(document_ref)?.dataURL
    // } else {
    //     const document = queryClient.getQueryData({
    //         queryKey: [
    //             'valuation',
    //             'document',
    //             document_ref,
    //             { variant: 'thumbnail' },
    //         ],
    //     })
    //     if (document !== undefined) {
    //         return document.dataURL
    //     } else {
    //         return 'document-image'
    //     }
    // }
  }

  /*
    ██████  ██    ██ ██████  ██████   ██████  ███████ ███████ ███████
    ██   ██ ██    ██ ██   ██ ██   ██ ██    ██ ██      ██      ██
    ██████  ██    ██ ██████  ██████  ██    ██ ███████ █████   ███████
    ██      ██    ██ ██   ██ ██      ██    ██      ██ ██           ██
    ██       ██████  ██   ██ ██       ██████  ███████ ███████ ███████
    */

  const byPurpose = (value: string) => {
    if (!value) {
      return documents.value
    }

    return documents.value.filter((d) => {
      if (!d.validation_purpose) {
        return d.purpose === value
      }
      else {
        return d.validation_purpose === value
      }
    })
  }

  const byPurposes = (value: DocumentPurpose[]) => {
    if (!value) {
      return documents.value
    }

    return documents.value.filter((d) => {
      if (!d.validation_purpose) {
        return value.includes(d.purpose as DocumentPurpose)
      }
      else {
        return value.includes(d.validation_purpose as DocumentPurpose)
      }
    })
  }

  const { accessibleModules } = useCurUser()

  const mandatoryErsReport = computed(
    () =>
      store.getters.getConfig.EPC_PROXY_REQUIRES_ERS_REPORT
      && curRequestRef.value.data.features.epc_source === enums.epcSource.USER_ESTIMATE,
  )

  const purposes = computed(() => {
    const config = store.getters.getConfig
    const noSalesAgreement = config.DISABLE_SALES_AGREEMENT_UPLOAD

    if (!config.ENABLE_DOCUMENTS_UPLOAD)
      return []
    if (!curRequestRef.value.isLoaded)
      return []

    const buildingType = curRequestRef.value.data.features.f_building_type
    const features = curRequestRef.value.data.features

    const basePurposesByType = {
      [enums.buildingTypes.HOUSE]: [
        enums.documentPurpose.EXTERIOR_PICTURE,
        enums.documentPurpose.INTERIOR_PICTURE,
      ],
      [enums.buildingTypes.APARTMENT]: [
        enums.documentPurpose.EXTERIOR_PICTURE,
        enums.documentPurpose.COMMON_PARTS_EPC,
        enums.documentPurpose.INTERIOR_PICTURE,
      ],
      [enums.buildingTypes.NEW_HOUSE]: [
        enums.documentPurpose.SPECIFICATIONS,
        enums.documentPurpose.BUILDING_BLUEPRINT,
        enums.documentPurpose.CADASTRAL_PLAN,
        enums.documentPurpose.EXPECTED_EPC,
        enums.documentPurpose.PROOF_OF_CONSTRUCTION,
      ],
      [enums.buildingTypes.NEW_APARTMENT]: [
        enums.documentPurpose.SPECIFICATIONS,
        enums.documentPurpose.BUILDING_BLUEPRINT,
        enums.documentPurpose.CADASTRAL_PLAN,
        enums.documentPurpose.EXPECTED_EPC,
        enums.documentPurpose.PROOF_OF_CONSTRUCTION,
      ],
      [enums.buildingTypes.CONSTRUCTION_PLOT]: [enums.documentPurpose.CADASTRAL_PLAN],
      [enums.buildingTypes.OTHER_PLOT]: [enums.documentPurpose.CADASTRAL_PLAN],
    }

    const conditionalRules = [
      {
        condition: () =>
          [enums.buildingTypes.HOUSE, enums.buildingTypes.APARTMENT].includes(buildingType)
          && !features.epc_unknown,
        purposes: [enums.documentPurpose.EPC_CERTIFICATE],
      },
      {
        condition: () =>
          buildingType === enums.buildingTypes.HOUSE
          && config.ALLOW_ERS_REPORT_UPLOAD
          && accessibleModules.value.includes('ers'),
        purposes: [enums.documentPurpose.ERS_REPORT],
      },
      {
        condition: () => mandatoryErsReport.value,
        purposes: [enums.documentPurpose.ERS_REPORT],
      },
      {
        condition: () =>
          [
            enums.buildingTypes.NEW_HOUSE,
            enums.buildingTypes.NEW_APARTMENT,
            enums.buildingTypes.CONSTRUCTION_PLOT,
          ].includes(buildingType) && !noSalesAgreement,
        purposes: [enums.documentPurpose.SALES_AGREEMENT],
      },
      {
        condition: () =>
          curRequestRef.value.is.existing
          && (features.energetic_renovation_cost > 0 || features.non_energetic_renovation_cost > 0),
        purposes: [enums.documentPurpose.RENOVATION_QUOTATION],
      },
      {
        condition: () => features.under_registration_rights,
        purposes: [enums.documentPurpose.BUILDING_BLUEPRINT, enums.documentPurpose.CADASTRAL_PLAN],
      },
    ]

    const basePurposes = basePurposesByType[buildingType] || []
    const conditionalPurposes = conditionalRules
      .filter(rule => rule.condition())
      .flatMap(rule => rule.purposes)

    return [...basePurposes, ...conditionalPurposes]
  })

  const requiredPurposes = computed(() => {
    if (!curRequestRef.value.isLoaded)
      return []

    // Implementing logic as defined here: https://github.com/rockestate/rockestate_vue/issues/4508#issuecomment-3327988956
    const purposes = []

    if (store.getters.getConfig.MANDATORY_EPC_CERTIFICATE && curRequestRef.value.is.existing) {
      purposes.push(enums.documentPurpose.EPC_CERTIFICATE)
    }

    if (mandatoryErsReport.value) {
      purposes.push(enums.documentPurpose.ERS_REPORT)
    }

    if (curRequestRef.value.is.new) {
      purposes.push(enums.documentPurpose.BUILDING_BLUEPRINT)
      purposes.push(enums.documentPurpose.SPECIFICATIONS)
    }

    if (
      (curRequestRef.value.data.features.main_parcel_not_found
        || curRequestRef.value.data.features.extra_parcels_not_found)
      && (curRequestRef.value.is.new || curRequestRef.value.is.plot)
    ) {
      purposes.push(enums.documentPurpose.CADASTRAL_PLAN)
    }

    if (curRequestRef.value.is.apartmentBlock) {
      purposes.push(enums.documentPurpose.EPC_CERTIFICATE)
    }

    return purposes
  })

  const missingPurposes = computed(() => requiredPurposes.value.filter(p => !byPurpose(p).length))
  const isMissingDocs = computed(() => missingPurposes.value.length > 0)

  return reactive({
    ...query,
    documents,
    rdocuments,
    requestQuery: curRequestRef,
    isLoaded: computed(() => query.data.value !== null && query.data.value !== undefined),
    isBusy: reactiveAny(query.isLoading, query.isFetching, mutation.isPending),
    hasDocuments: computed(() => documents.value.length > 0),

    uploadDoc,
    deleteDoc,
    patchDoc,
    addDoc,

    createStreetView,

    byRef,
    byPurpose,
    byPurposes,
    purposes,
    requiredPurposes,
    missingPurposes,
    isMissingDocs,

    getImageDataURL,
  })
}

export default useDocumentList
